import {
  computed,
  inject,
  type InjectionKey,
  provide,
  type Ref,
  toValue,
  type MaybeRefOrGetter,
} from "vue";
import {
  useSQDCWorkflowDefinitionsQuery,
  useSQDCWorkflowInstancesQuery,
  type SQDCWorkflowDefinitionsQueryVariables,
  type SQDCWorkflowInstancesQueryVariables,
} from "@/graphql";
import { toSQDCWorkflowDefinition, toSQDCWorkflowInstance } from "./repository";
import type { SQDCWorkflowInstance } from "./domain";

type Variables<T> = T | Ref<T> | (() => T);

const SQDCWorkflowInstancesBatchInjectionKey: InjectionKey<Ref<SQDCWorkflowInstance[]>> = Symbol(
  "SQDCWorkflowInstancesBatch",
);

export function useSQDCWorkflowDefinitions(
  variables: Variables<SQDCWorkflowDefinitionsQueryVariables> = {},
) {
  const query = useSQDCWorkflowDefinitionsQuery(variables, {
    // Definitions are immutable versions. Start menus must still check the server for the
    // current, non-superseded version instead of reusing a cached predecessor indefinitely.
    fetchPolicy: "cache-and-network",
  });
  const definitions = computed(
    () => query.result.value?.SQDCWorkflowDefinitions.map(toSQDCWorkflowDefinition) ?? [],
  );
  return { ...query, definitions };
}

export function useSQDCWorkflowInstances(
  variables: Variables<SQDCWorkflowInstancesQueryVariables> = {},
) {
  const query = useSQDCWorkflowInstancesQuery(variables, {
    fetchPolicy: "cache-and-network",
  });
  const instances = computed(
    () => query.result.value?.SQDCWorkflowInstances.map(toSQDCWorkflowInstance) ?? [],
  );
  return { ...query, instances };
}

export function provideSQDCWorkflowInstancesBatch(
  eventIds: MaybeRefOrGetter<number[]>,
  actionIds: MaybeRefOrGetter<number[]>,
) {
  const variables = computed(() => ({
    eventIds: toValue(eventIds),
    actionIds: toValue(actionIds),
  }));
  const query = useSQDCWorkflowInstancesQuery(variables, () => ({
    enabled: variables.value.eventIds.length > 0 || variables.value.actionIds.length > 0,
    fetchPolicy: "cache-and-network",
  }));
  const instances = computed(
    () => query.result.value?.SQDCWorkflowInstances.map(toSQDCWorkflowInstance) ?? [],
  );
  provide(SQDCWorkflowInstancesBatchInjectionKey, instances);
  return { ...query, instances };
}

export function useSQDCWorkflowInstancesForItem(
  variables: Variables<SQDCWorkflowInstancesQueryVariables>,
) {
  const batchedInstances = inject(SQDCWorkflowInstancesBatchInjectionKey, undefined);
  const query = useSQDCWorkflowInstancesQuery(variables, {
    enabled: batchedInstances == null,
    fetchPolicy: "cache-and-network",
  });
  const resolvedVariables = computed(() => toValue(variables));
  const instances = computed(() => {
    if (batchedInstances == null) {
      return query.result.value?.SQDCWorkflowInstances.map(toSQDCWorkflowInstance) ?? [];
    }
    const { eventId, actionId } = resolvedVariables.value;
    return batchedInstances.value.filter(
      (instance) =>
        (eventId != null && instance.eventIds.includes(eventId)) ||
        (actionId != null && instance.actionIds.includes(actionId)),
    );
  });
  return { ...query, instances };
}
